import { Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { STEP_LABELS, STEP_STYLES } from "./constants";
import {
  CheckoutStepFields,
  EmailStepFields,
  InstagramButtonTextInput,
  InstagramDmBodyTextarea,
  LayloCreateSubscriberFields,
  ManychatCreateSubscriberFields,
  BeehiivCreateSubscriberFields,
  MailerLiteCreateSubscriberFields,
  MetaAddToAudienceFields,
  PageStepFields,
  SmsStepBody,
  WaitStepFields,
} from "./StepFieldComponents";
import { isAutomationStepCheckout, isAutomationStepPage } from "@/types/Automation";
import type { StepNodeData } from "./types";

export function StepNode({ data }: NodeProps<Node<StepNodeData>>) {
  const {
    step,
    onStepDataChange,
    onStepButtonsChange,
    onMailerLiteGroupIdsChange,
    onManychatTagIdsChange,
    onBeehiivAutomationIdsChange,
    onMetaAddToAudienceChange,
    onPageStepChange,
    onCheckoutStepChange,
    onDeleteStep,
    onEditEmailBody,
    artistTimezone,
  } = data;
  const style = STEP_STYLES[step.type];
  const Icon = style.icon;

  return (
    <div className={`bg-white rounded-xl border-2 ${style.border} shadow-md w-[280px] relative pb-10`}>
      <Handle
        type="target"
        position={Position.Left}
        className={`${style.handle} !w-3 !h-3 !border-2 !border-white`}
        style={{ top: "22px", left: 0 }}
      />
      <div className={`flex items-center justify-between px-4 py-3 ${style.bg} rounded-t-xl border-b ${style.border}`}>
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${style.text}`} />
          <span className={`font-semibold text-sm ${style.text}`}>
            {STEP_LABELS[step.type]}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onDeleteStep(step.id)}
          onPointerDownCapture={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {step.type === "sms" && <SmsStepBody step={step} onStepDataChange={onStepDataChange} />}
      {step.type === "instagram.sendMessage" && (
        <div
          className="p-4 space-y-4 nodrag"
          onPointerDownCapture={(e) => {
            if ((e.target as HTMLElement).closest(".react-flow__handle")) return;
            e.stopPropagation();
          }}
        >
          <InstagramDmBodyTextarea step={step} onStepDataChange={onStepDataChange} />
          <div>
            <div className="text-xs font-medium text-pink-700 mb-2">Buttons</div>
            <div className="space-y-2">
              {(step.data?.buttons ?? []).map((btn, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <InstagramButtonTextInput
                    step={step}
                    buttonIndex={i}
                    text={btn.text}
                    onStepButtonsChange={onStepButtonsChange}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (step.data?.buttons ?? []).filter((_, j) => j !== i);
                      onStepButtonsChange({ stepId: step.id, buttons: next });
                    }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`button-${i}`}
                    className="!bg-pink-400 !border-2 !border-pink-200 hover:!bg-pink-500 !rounded-full"
                    style={{
                      position: "relative",
                      top: "auto",
                      right: "auto",
                      transform: "none",
                      width: 14,
                      height: 14,
                      minWidth: 14,
                      cursor: "crosshair",
                    }}
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full text-sm"
                onClick={() =>
                  onStepButtonsChange({
                    stepId: step.id,
                    buttons: [...(step.data?.buttons ?? []), { text: "" }],
                  })
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add button
              </Button>
            </div>
          </div>
        </div>
      )}
      {step.type === "email" && (
        <EmailStepFields step={step} onStepDataChange={onStepDataChange} onEditEmailBody={onEditEmailBody} />
      )}
      {step.type === "wait" && (
        <WaitStepFields step={step} onStepDataChange={onStepDataChange} artistTimezone={artistTimezone} />
      )}
      {step.type === "mailerlite.createSubscriber" && (
        <MailerLiteCreateSubscriberFields step={step} onMailerLiteGroupIdsChange={onMailerLiteGroupIdsChange} />
      )}
      {step.type === "laylo.createSubscriber" && (
        <LayloCreateSubscriberFields step={step} onStepDataChange={onStepDataChange} />
      )}
      {step.type === "manychat.createSubscriber" && (
        <ManychatCreateSubscriberFields step={step} onManychatTagIdsChange={onManychatTagIdsChange} />
      )}
      {step.type === "beehiiv.createSubscriber" && (
        <BeehiivCreateSubscriberFields step={step} onBeehiivAutomationIdsChange={onBeehiivAutomationIdsChange} />
      )}
      {step.type === "meta.addToAudience" && (
        <MetaAddToAudienceFields step={step} onMetaAddToAudienceChange={onMetaAddToAudienceChange} />
      )}
      {isAutomationStepPage(step) && (
        <PageStepFields step={step} onPageStepChange={onPageStepChange} />
      )}
      {isAutomationStepCheckout(step) && (
        <CheckoutStepFields
          step={step}
          onCheckoutStepChange={onCheckoutStepChange}
        />
      )}
      {step.type !== "page" && step.type !== "checkout" && (
        <div className="absolute bottom-2 right-0 flex items-center nodrag">
          <span className={`py-0.5 pr-2 rounded-md bg-white/80 text-xs font-semibold ${style.text}`}>Then</span>
          <Handle
            id={step.type === "instagram.sendMessage" ? "then" : undefined}
            type="source"
            position={Position.Right}
            className={`${style.handle} !w-3 !h-3 !border-2 !border-white`}
          />
        </div>
      )}
    </div>
  );
}
